/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useTranslation} from "react-i18next";
import {PersonType} from "types/enums/PersonType";
import {useTypedSelector} from "helpers/reducers";
import Loader from "components/Loader";
import {HallCode} from "types/enums/HallCode";
import clsx from "clsx";
import {useHallOpeningHours} from "utils/hooks/useHallOpeningHours";
import {usePackageCategory} from "utils/hooks/usePackageCategory";
import {PackageCategory} from "types/enums/PackageCategory";

interface DriverCountPersonTypeProps {
    minimumDriversCount: number;
    personType: PersonType;
    isEvent: boolean | undefined;
    isAction: boolean | undefined;
    isGroupTypeChildren: boolean | undefined;
    isMinimalistic: boolean;
    classNameTitle?: string;
}

const DriverCountPersonType = (props: DriverCountPersonTypeProps) => {
    const {t} = useTranslation();
    const hall = useTypedSelector(state => state.hallState.hall);
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const {packageCategoryPreSummer} = useHallOpeningHours();
    const {getPackageCategoryDriversCountText} = usePackageCategory();

    if (!hall) {
        return <Loader />;
    }

    if (hall.hall_code === HallCode.ArboPark && (props.isEvent || (!props.isEvent && !props.isAction))) {
        return (
            <div className={"flex flex-col w-full mb-2 sm:mb-0 items-center sm:items-start sm:w-1/2 md:w-1/2"}>
                <div className={"capitalize flex flex-col items-start"}>
                    <span className={clsx("PersonType leading-4 font-semibold", props.classNameTitle)}>{t("Persons")}</span>
                    <div className={"text-sm"}>
                        {props.minimumDriversCount > 0 && <span>{t("minimum_persons_count", {count: props.minimumDriversCount})}</span>}
                    </div>
                </div>
                {!props.isMinimalistic && (
                    <span className={"text-xs mt-1 break-words text-gray-600 text-center sm:text-left"}>
                        {t("From 8 to 11 years old only when accompanied by an adult")}
                    </span>
                )}
            </div>
        );
    }

    const getPersonTypeLabel = (personType: PersonType) => {
        switch (personType) {
            case PersonType.Adult:
                if (bookingFilter.category === PackageCategory.School) {
                    return <span>{t("18+ years")}</span>;
                }
                if (packageCategoryPreSummer()) {
                    return <span>{t("18+ years")}</span>;
                }
                return <span>{t("16+ years")}</span>;
            case PersonType.Child:
                if (getPackageCategoryDriversCountText(bookingFilter.category as PackageCategory, personType)) {
                    if (packageCategoryPreSummer()) {
                        return (
                            <span>
                                {t("From 8 to 17 years")}
                                <br />
                                {t("Children are accompanied by adult person from Arbo Park personal")}
                            </span>
                        );
                    }
                    return (
                        <span>
                            {t("From 8 to 12 years")}
                            <br />
                            {t("Children are accompanied by adult person from Arbo Park personal")}
                        </span>
                    );
                }
                if (packageCategoryPreSummer()) {
                    return (
                        <span>
                            {t("From 8 to 17 years")}
                            <br />
                            {t("From 8 to 11 years old only when accompanied by an adult")}
                        </span>
                    );
                }
                if (bookingFilter.category === PackageCategory.MegaFunSchool) {
                    return <span>{t("From 8 to 17 years")}</span>;
                }

                if (bookingFilter.category === PackageCategory.Kinder) {
                    return <span>{t("From 8 to 15 years")}</span>;
                }
                if (bookingFilter.category === PackageCategory.FerienPass) {
                    return (
                        <span className={"FerienPass"}>
                            {t("From 8 to 17 years")}
                            <br />
                            {t("From 8 to 11 years old only when accompanied by an adult")}
                        </span>
                    );
                }
                return (
                    <span>
                        {t("From 8 to 15 years")}
                        <br />
                        {t("From 8 to 11 years old only when accompanied by an adult")}
                    </span>
                );
        }
    };

    return (
        <div className={"flex flex-col w-full mb-2 sm:mb-0 items-center sm:items-start sm:w-1/2 md:w-1/2"}>
            <div className={"capitalize flex flex-col items-start"}>
                <span className={clsx("PersonType leading-4 font-bold", props.classNameTitle)}>
                    {!props.isGroupTypeChildren && props.isEvent ? t("Number of people") : t(props.personType)}
                </span>

                <div className={"flex flex-col text-sm text-gray-800 text-center sm:text-left"}>
                    {![PackageCategory.MegaFunSchool, PackageCategory.SchoolTrophy].includes(bookingFilter.category as PackageCategory) &&
                        props.minimumDriversCount > 0 && <span>{t("minimum_persons_count", {count: props.minimumDriversCount})}</span>}
                    {[PackageCategory.MegaFunSchool, PackageCategory.SchoolTrophy].includes(bookingFilter.category as PackageCategory) &&
                        props.personType == PersonType.Adult && <span>{t("Maximum 25% Adults allowed")}</span>}
                </div>
            </div>
            {!props.isMinimalistic && (
                <span className={"text-xs text-gray-600 break-words text-center sm:text-left"}>{getPersonTypeLabel(props.personType)}</span>
            )}
        </div>
    );
};

export default DriverCountPersonType;
